import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Search, Eye, Trash2, Clock, CheckCircle, ShoppingCart, XCircle, X, MessageSquare, User, Phone, Mail, MapPin, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Pagination } from '../components/Pagination';
import { quotesAPI } from '../lib/api';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { toast } from 'sonner';

const QUOTE_STATUS = {
    pending: { label: 'En attente', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
    responded: { label: 'Repondu', icon: CheckCircle, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    converted: { label: 'Converti', icon: ShoppingCart, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
    cancelled: { label: 'Annule', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200' },
};

const StatusBadge = ({ status }) => {
    const cfg = QUOTE_STATUS[status] || QUOTE_STATUS.pending;
    const Icon = cfg.icon;
    return (
        <Badge variant="outline" className={`rounded-full text-xs font-medium ${cfg.color}`} data-testid={`quote-status-${status}`}>
            <Icon className="w-3 h-3 mr-1" />
            {cfg.label}
        </Badge>
    );
};

export const AdminQuotes = () => {
    const [quotes, setQuotes] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalQuotes, setTotalQuotes] = useState(0);
    const ITEMS_PER_PAGE = 50;

    // Detail dialog
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Respond dialog
    const [respondOpen, setRespondOpen] = useState(false);
    const [respondQuote, setRespondQuote] = useState(null);
    const [respondStatus, setRespondStatus] = useState('responded');
    const [respondNote, setRespondNote] = useState('');

    // Delete dialog
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [quoteToDelete, setQuoteToDelete] = useState(null);

    // Expanded row on mobile
    const [expandedId, setExpandedId] = useState(null);

    const loadQuotes = useCallback(async () => {
        try {
            const skip = (currentPage - 1) * ITEMS_PER_PAGE;
            const params = { skip, limit: ITEMS_PER_PAGE };
            if (searchQuery) params.search = searchQuery;
            if (statusFilter !== 'all') params.status = statusFilter;
            const res = await quotesAPI.list(params);
            setQuotes(res.data.quotes || []);
            setTotalQuotes(res.data.total || 0);
        } catch (err) {
            console.error('Error loading quotes:', err);
            toast.error('Erreur lors du chargement des demandes de prix');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery, statusFilter]);

    const loadStats = useCallback(async () => {
        try {
            const res = await quotesAPI.getStats();
            setStats(res.data);
        } catch (err) { console.error('Error loading quote stats:', err); }
    }, []);

    useEffect(() => {
        loadQuotes();
        loadStats();
        api.put('/admin/orders/mark-seen').catch(() => {});
    }, [loadQuotes, loadStats]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(search);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleViewDetail = async (quote) => {
        try {
            const res = await quotesAPI.getDetail(quote.id);
            setSelectedQuote(res.data);
            setDetailOpen(true);
        } catch {
            toast.error('Erreur lors du chargement du detail');
        }
    };

    const handleOpenRespond = (quote) => {
        setRespondQuote(quote);
        setRespondStatus(quote.status === 'pending' ? 'responded' : quote.status);
        setRespondNote(quote.response_note || '');
        setRespondOpen(true);
    };

    const handleRespond = async () => {
        if (!respondQuote) return;
        try {
            await quotesAPI.respond(respondQuote.id, {
                status: respondStatus,
                response_note: respondNote,
            });
            toast.success('Statut de la demande mis a jour');
            setRespondOpen(false);
            loadQuotes();
            loadStats();
        } catch {
            toast.error('Erreur lors de la mise a jour');
        }
    };

    const handleDeleteClick = (quote) => {
        setQuoteToDelete(quote);
        setDeleteOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!quoteToDelete) return;
        try {
            await quotesAPI.delete(quoteToDelete.id);
            toast.success('Demande supprimee');
            setDeleteOpen(false);
            setQuoteToDelete(null);
            loadQuotes();
            loadStats();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const totalPages = Math.ceil(totalQuotes / ITEMS_PER_PAGE);

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-quotes-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">
                                Demandes de prix
                            </h1>
                            <p className="text-muted-foreground">{totalQuotes} demande{totalQuotes !== 1 ? 's' : ''} au total</p>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl" data-testid="quotes-stat-total">
                            <CardContent className="pt-4 pb-4 text-center">
                                <p className="text-2xl font-bold text-jungle">{stats.total}</p>
                                <p className="text-xs text-muted-foreground mt-1">Total</p>
                            </CardContent>
                        </Card>
                        <Card className="border-0 bg-amber-50/80 shadow-sm rounded-2xl" data-testid="quotes-stat-pending">
                            <CardContent className="pt-4 pb-4 text-center">
                                <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
                                <p className="text-xs text-amber-600 mt-1">En attente</p>
                            </CardContent>
                        </Card>
                        <Card className="border-0 bg-blue-50/80 shadow-sm rounded-2xl" data-testid="quotes-stat-responded">
                            <CardContent className="pt-4 pb-4 text-center">
                                <p className="text-2xl font-bold text-blue-700">{stats.responded}</p>
                                <p className="text-xs text-blue-600 mt-1">Repondus</p>
                            </CardContent>
                        </Card>
                        <Card className="border-0 bg-emerald-50/80 shadow-sm rounded-2xl" data-testid="quotes-stat-converted">
                            <CardContent className="pt-4 pb-4 text-center">
                                <p className="text-2xl font-bold text-emerald-700">{stats.converted}</p>
                                <p className="text-xs text-emerald-600 mt-1">Convertis</p>
                            </CardContent>
                        </Card>
                        <Card className="border-0 bg-red-50/80 shadow-sm rounded-2xl" data-testid="quotes-stat-cancelled">
                            <CardContent className="pt-4 pb-4 text-center">
                                <p className="text-2xl font-bold text-red-700">{stats.cancelled}</p>
                                <p className="text-xs text-red-600 mt-1">Annules</p>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Filters */}
                <Card className="mb-6 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardContent className="pt-6">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1 flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher par n° demande, entreprise, email..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-10 bg-white/80 border-jungle/20 rounded-xl"
                                        data-testid="quotes-search-input"
                                    />
                                </div>
                                {search && (
                                    <Button variant="ghost" onClick={() => setSearch('')} className="text-destructive hover:bg-destructive/10 rounded-xl">
                                        <X className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                                <SelectTrigger className="w-full sm:w-[180px] bg-white/80 border-jungle/20 rounded-xl" data-testid="quotes-status-filter">
                                    <SelectValue placeholder="Filtrer par statut" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les statuts</SelectItem>
                                    {Object.entries(QUOTE_STATUS).map(([key, { label }]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Quotes Table */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading text-jungle">
                            {totalQuotes} demande{totalQuotes !== 1 ? 's' : ''} de prix
                            {searchQuery && <span className="text-sm font-normal text-muted-foreground ml-2">pour "{searchQuery}"</span>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {quotes.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-jungle/10">
                                            <TableHead className="text-jungle/70">N° Demande</TableHead>
                                            <TableHead className="text-jungle/70">Client</TableHead>
                                            <TableHead className="text-jungle/70 hidden md:table-cell">Contact</TableHead>
                                            <TableHead className="text-jungle/70">Date</TableHead>
                                            <TableHead className="text-jungle/70 text-center">Produits</TableHead>
                                            <TableHead className="text-jungle/70">Statut</TableHead>
                                            <TableHead className="text-jungle/70 text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {quotes.map((q) => (
                                            <React.Fragment key={q.id}>
                                                <TableRow className="border-jungle/10 cursor-pointer hover:bg-leaf/5" data-testid={`quote-row-${q.id}`} onClick={() => handleViewDetail(q)}>
                                                    <TableCell>
                                                        <span className="font-mono text-sm font-medium text-jungle">{q.quote_number}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium text-jungle text-sm">{q.company_name}</p>
                                                            {q.client_code && <p className="text-xs text-muted-foreground">{q.client_code}</p>}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell">
                                                        <p className="text-sm text-muted-foreground">{q.contact_name}</p>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="text-sm text-muted-foreground">{formatDateTime(q.created_at)}</span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <Badge variant="outline" className="rounded-full border-jungle/20">
                                                            {q.items?.length || 0}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusBadge status={q.status} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                                            <Button variant="ghost" size="sm" className="hover:bg-leaf/10" onClick={() => handleViewDetail(q)} data-testid={`quote-view-${q.id}`}>
                                                                <Eye className="h-4 w-4 text-leaf" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" className="hover:bg-blue-100" onClick={() => handleOpenRespond(q)} data-testid={`quote-respond-${q.id}`}>
                                                                <MessageSquare className="h-4 w-4 text-blue-600" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => handleDeleteClick(q)} data-testid={`quote-delete-${q.id}`}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <FileText className="w-12 h-12 mx-auto mb-4 text-jungle/20" />
                                <p className="text-muted-foreground">Aucune demande de prix trouvee</p>
                            </div>
                        )}
                    </CardContent>
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalQuotes}
                        setCurrentPage={setCurrentPage}
                        label="demandes de prix"
                    />
                </Card>
            </div>

            {/* Detail Dialog */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-sand" data-testid="quote-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-jungle flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                <FileText className="w-4 h-4 text-amber-700" />
                            </div>
                            Demande {selectedQuote?.quote_number}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedQuote && (
                        <div className="space-y-6">
                            {/* Status */}
                            <div className="flex items-center justify-between">
                                <StatusBadge status={selectedQuote.status} />
                                <span className="text-sm text-muted-foreground">{formatDateTime(selectedQuote.created_at)}</span>
                            </div>

                            {/* Client Info */}
                            <div className="p-4 rounded-xl bg-white/60 border border-jungle/10">
                                <h3 className="text-sm font-semibold text-jungle mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4" /> Informations client
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Entreprise:</span>
                                        <span className="ml-2 font-medium text-jungle">{selectedQuote.company_name}</span>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Contact:</span>
                                        <span className="ml-2 text-jungle">{selectedQuote.contact_name}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Mail className="w-3 h-3 text-muted-foreground" />
                                        <span className="text-jungle">{selectedQuote.email}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Phone className="w-3 h-3 text-muted-foreground" />
                                        <span className="text-jungle">{selectedQuote.phone || 'N/A'}</span>
                                    </div>
                                    {selectedQuote.client_code && (
                                        <div>
                                            <span className="text-muted-foreground">Code:</span>
                                            <Badge variant="outline" className="ml-2 font-mono text-xs rounded-full">{selectedQuote.client_code}</Badge>
                                        </div>
                                    )}
                                    {selectedQuote.delivery_address && (
                                        <div className="sm:col-span-2 flex items-start gap-1">
                                            <MapPin className="w-3 h-3 text-muted-foreground mt-0.5" />
                                            <span className="text-jungle">{selectedQuote.delivery_address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Products */}
                            <div className="p-4 rounded-xl bg-white/60 border border-jungle/10">
                                <h3 className="text-sm font-semibold text-jungle mb-3 flex items-center gap-2">
                                    <Package className="w-4 h-4" /> Produits demandes ({selectedQuote.items?.length || 0})
                                </h3>
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="text-jungle/70">Produit</TableHead>
                                                <TableHead className="text-jungle/70 text-center">Quantite</TableHead>
                                                <TableHead className="text-jungle/70 text-center">Unite</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {selectedQuote.items?.map((item, idx) => (
                                                <TableRow key={idx} className="border-jungle/10">
                                                    <TableCell className="font-medium text-jungle">{item.product_name}</TableCell>
                                                    <TableCell className="text-center">{item.quantity}</TableCell>
                                                    <TableCell className="text-center text-muted-foreground">{item.unit}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>

                            {/* Notes */}
                            {selectedQuote.notes && (
                                <div className="p-4 rounded-xl bg-amber-50/60 border border-amber-200">
                                    <h3 className="text-sm font-semibold text-amber-800 mb-2">Notes du client</h3>
                                    <p className="text-sm text-amber-900">{selectedQuote.notes}</p>
                                </div>
                            )}

                            {/* Response info */}
                            {selectedQuote.responded_by && (
                                <div className="p-4 rounded-xl bg-blue-50/60 border border-blue-200">
                                    <h3 className="text-sm font-semibold text-blue-800 mb-2">Reponse admin</h3>
                                    <p className="text-xs text-blue-600 mb-1">
                                        Par {selectedQuote.responded_by} le {selectedQuote.responded_at ? formatDateTime(selectedQuote.responded_at) : 'N/A'}
                                    </p>
                                    {selectedQuote.response_note && (
                                        <p className="text-sm text-blue-900">{selectedQuote.response_note}</p>
                                    )}
                                </div>
                            )}

                            <DialogFooter className="gap-2">
                                <Button variant="outline" onClick={() => setDetailOpen(false)} className="rounded-xl">
                                    Fermer
                                </Button>
                                <Button className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl" onClick={() => { setDetailOpen(false); handleOpenRespond(selectedQuote); }}>
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Repondre / Changer statut
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Respond Dialog */}
            <Dialog open={respondOpen} onOpenChange={setRespondOpen}>
                <DialogContent className="max-w-md bg-sand" data-testid="quote-respond-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-jungle flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                <MessageSquare className="w-4 h-4 text-blue-700" />
                            </div>
                            Repondre a la demande {respondQuote?.quote_number}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-jungle block mb-2">Nouveau statut</label>
                            <Select value={respondStatus} onValueChange={setRespondStatus}>
                                <SelectTrigger className="border-jungle/20 rounded-xl" data-testid="respond-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(QUOTE_STATUS).map(([key, { label }]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-jungle block mb-2">Note (optionnel)</label>
                            <Textarea
                                value={respondNote}
                                onChange={(e) => setRespondNote(e.target.value)}
                                placeholder="Ajouter une note interne sur cette demande..."
                                className="border-jungle/20 rounded-xl min-h-[100px]"
                                data-testid="respond-note-input"
                            />
                        </div>
                        <DialogFooter className="gap-2">
                            <Button variant="outline" onClick={() => setRespondOpen(false)} className="rounded-xl">
                                Annuler
                            </Button>
                            <Button className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl" onClick={handleRespond} data-testid="respond-submit-btn">
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent className="bg-sand">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-jungle">Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Supprimer la demande de prix <strong>{quoteToDelete?.quote_number}</strong> de {quoteToDelete?.company_name} ?
                            Cette action est irreversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-destructive hover:bg-destructive/90 rounded-xl"
                            data-testid="quote-delete-confirm"
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
